/**
 * TEMPORARY client for the shadow-audio debug capture. Gated entirely by the
 * backend (CAPTURE_SHADOW_AUDIO): the enabled-check is fetched once per page
 * load and cached, so when the flag is off this is a single cheap request and
 * nothing is ever captured or uploaded. Remove with the backend debug routes
 * when tuning validation is done.
 */
import { globalApiConfig } from 'careand-redux/api/ApiThunks';

function authHeaders(): Record<string, string> {
  const auth = globalApiConfig.apiKey?.('Bearer') ?? '';
  return {
    Authorization: typeof auth === 'string' ? auth : '',
    ...(globalApiConfig.headers as Record<string, string>),
  };
}

let enabledPromise: Promise<boolean> | undefined;

/** Memoized: at most one request per page load; resolves false on any error. */
export function isShadowCaptureEnabled(): Promise<boolean> {
  if (!enabledPromise) {
    enabledPromise = fetch(`${globalApiConfig.basePath}/debug/shadow-audio/enabled`, {
      headers: authHeaders(),
    })
      .then((r) => (r.ok ? r.json() : { enabled: false }))
      .then((j) => !!j?.enabled)
      .catch(() => false);
  }
  return enabledPromise;
}

/** Fire-and-forget WAV upload. Never throws — the shadow must not be affected. */
export async function uploadShadowAudio(wav: ArrayBuffer): Promise<void> {
  try {
    await fetch(`${globalApiConfig.basePath}/debug/shadow-audio`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/octet-stream' },
      body: wav,
    });
  } catch {
    /* swallow — capture is best-effort debug only */
  }
}

/** Build a 16-bit PCM WAV (little-endian) from raw linear16 chunks. */
export function buildWav(chunks: Uint8Array[], sampleRate: number, channels: number): ArrayBuffer {
  const dataLen = chunks.reduce((n, c) => n + c.byteLength, 0);
  const buffer = new ArrayBuffer(44 + dataLen);
  const view = new DataView(buffer);
  const writeStr = (off: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i));
  };
  const byteRate = sampleRate * channels * 2;
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + dataLen, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, channels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, channels * 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  writeStr(36, 'data');
  view.setUint32(40, dataLen, true);
  let offset = 44;
  for (const c of chunks) {
    new Uint8Array(buffer, offset, c.byteLength).set(c);
    offset += c.byteLength;
  }
  return buffer;
}
